import type React from 'react';
import type { SvgProps } from 'react-native-svg';

// Community Resource Icons
import BooksIcon from '@/assets/admin/CommunityResourceIcons/books.svg';
import BriefcaseIcon from '@/assets/admin/CommunityResourceIcons/briefcase.svg';
import CalendarIcon from '@/assets/admin/CommunityResourceIcons/calendar.svg';
import CarIcon from '@/assets/admin/CommunityResourceIcons/car.svg';
import GlobeIcon from '@/assets/admin/CommunityResourceIcons/globe.svg';
import MusicIcon from '@/assets/admin/CommunityResourceIcons/music.svg';
import QuestionMarkIcon from '@/assets/admin/CommunityResourceIcons/questionMark.svg';
import WrenchIcon from '@/assets/admin/CommunityResourceIcons/wrench.svg';

// Admin
import MagnifyingGlassIcon from '@/assets/admin/magnifyingGlass.svg';

// Residents
import CelebrationIcon from '@/assets/residents/celebration.svg';
import HouseIcon from '@/assets/residents/house.svg';
import MarketplaceStarIcon from '@/assets/residents/marketplace-star.svg';
import QuestionBubbleIcon from '@/assets/residents/questionBubble.svg';

export type TagIconComponent = React.FC<SvgProps & { color?: string }>;

export interface TagIconOption {
  /** Persisted value of `tag.icon` — an emoji glyph (storage stays unchanged). */
  value: string;
  /** Accessible/display label for the picker cell. */
  label: string;
  /** SVG component to render in the pill and picker. */
  Component: TagIconComponent;
}

/**
 * The 13 supported tag icons. Storage value remains the emoji glyph;
 * display layer renders the SVG.
 */
export const TAG_ICON_OPTIONS: readonly TagIconOption[] = [
  { value: '💬', label: 'Discussion', Component: QuestionBubbleIcon },
  { value: '🛠️', label: 'Tools', Component: WrenchIcon },
  { value: '💼', label: 'Work', Component: BriefcaseIcon },
  { value: '📅', label: 'Events', Component: CalendarIcon },
  { value: '🔍', label: 'Search', Component: MagnifyingGlassIcon },
  { value: '⭐', label: 'Featured', Component: MarketplaceStarIcon },
  { value: '📢', label: 'Announcement', Component: GlobeIcon },
  { value: '📚', label: 'Books', Component: BooksIcon },
  { value: '🎉', label: 'Celebration', Component: CelebrationIcon },
  { value: '🏠', label: 'Home', Component: HouseIcon },
  { value: '🚗', label: 'Vehicle', Component: CarIcon },
  { value: '🎵', label: 'Music', Component: MusicIcon },
  { value: '❓', label: 'Question', Component: QuestionMarkIcon },
] as const;

const ICON_BY_VALUE = new Map<string, TagIconComponent>(
  TAG_ICON_OPTIONS.map((opt) => [opt.value, opt.Component]),
);

/**
 * Resolves a stored `tag.icon` value to an SVG component.
 * Returns null if the value is empty or doesn't match any of the 13 supported icons —
 * caller should fall back to rendering the value as text in that case.
 */
export function getTagIconComponent(value: string | null): TagIconComponent | null {
  if (!value) return null;
  return ICON_BY_VALUE.get(value) ?? null;
}
